import React, { useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { ChevronDown, Play } from 'lucide-react';
import { motion } from 'framer-motion';
import { createSectionTracker } from '../../services/funnelTracker';

interface HeroV2Props {
  onScrollToVideo: () => void;
}

export default function HeroV2({ onScrollToVideo }: HeroV2Props) {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const tracker = createSectionTracker('hero');
    if (sectionRef.current) tracker.ref(sectionRef.current);
    return () => tracker.cleanup();
  }, []);

  return (
    <section ref={sectionRef} className="min-h-screen flex items-center justify-center px-4 sm:px-6">
      <div className="max-w-5xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-block mb-6 sm:mb-8"
          >
            <span className="bg-green-500/20 border border-green-500/40 text-green-400 px-4 py-2 rounded-full text-sm font-medium">
              Bootcamp Data Engineering — Cupos limitados
            </span>
          </motion.div>

          {/* Headline */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-green-400 mb-4 sm:mb-6 leading-tight">
            Convertite en Data Engineer
            <br />
            <span className="text-white">y trabajá remoto para empresas globales</span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-8 sm:mb-10 leading-relaxed">
            Sin más cursos teóricos. Con <span className="text-green-400 font-semibold">proyecto real</span>,{' '}
            <span className="text-green-400 font-semibold">SQL avanzado</span> y{' '}
            <span className="text-green-400 font-semibold">Databricks</span>. Bootcamp de 10 semanas.
          </p>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            <Button
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-5 sm:px-10 sm:py-6 text-base sm:text-lg md:text-xl rounded-full shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
              onClick={onScrollToVideo}
            >
              <Play className="mr-2 w-5 h-5 sm:w-6 sm:h-6" />
              Mirá mi historia
            </Button>
          </motion.div>

          {/* Trust indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="mt-10 sm:mt-12 flex flex-wrap justify-center gap-6 sm:gap-8 text-sm text-gray-400"
          >
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full" />
              +300 alumnos capacitados
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full" />
              +40 profesionales mentoreados
            </span>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.5 }}
          className="mt-16 sm:mt-20"
        >
          <button
            onClick={onScrollToVideo}
            className="text-green-400 hover:text-green-300 transition-colors animate-bounce"
            aria-label="Scroll al video"
          >
            <ChevronDown className="w-8 h-8 mx-auto" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
